import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Image as ImageIcon, Loader2, X, ArrowRight, CameraOff } from 'lucide-react';
import type { Chiamata } from '../shared/types';
import { compressImages } from '../utils/imageCompress';

interface FotoDisservizioModalProps {
  isOpen: boolean;
  chiamata: Chiamata | null;
  onConfirm: (files: File[]) => Promise<void>;
  onSkip: () => void;
  isUploading: boolean;
}

export function FotoDisservizioModal({
  isOpen,
  chiamata,
  onConfirm,
  onSkip,
  isUploading,
}: FotoDisservizioModalProps) {
  const [fotoFiles, setFotoFiles] = useState<File[]>([]);
  const [compressing, setCompressing] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  if (!isOpen || !chiamata) return null;
  if (typeof document === 'undefined') return null;

  const handleAddPhotos = async (newFiles: File[]) => {
    if (newFiles.length === 0) return;
    setCompressing(true);
    try {
      const compressi = await compressImages(newFiles);
      setFotoFiles((prev) => [...prev, ...compressi].slice(0, 10));
    } catch {
      setFotoFiles((prev) => [...prev, ...newFiles].slice(0, 10));
    } finally {
      setCompressing(false);
    }
  };

  const handleRemovePhoto = (index: number) => {
    setFotoFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (fotoFiles.length === 0) {
      onSkip();
      return;
    }
    await onConfirm(fotoFiles);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-slate-900/80 backdrop-blur-xs animate-fade-in safe-bottom">
      <div className="w-full max-w-md rounded-2xl sm:rounded-3xl bg-white shadow-2xl flex flex-col max-h-[90dvh] sm:max-h-[85vh] overflow-hidden animate-slide-up border border-slate-200">
        {/* Header fisso */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm">
              📸
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-extrabold text-slate-900 leading-tight">
                Foto Stato Disservizio
              </h3>
              <p className="text-xs text-slate-500">
                Arrivo sul posto per l'impianto {chiamata.impianto?.codice}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-ghost p-1.5 text-slate-400 hover:text-slate-600 rounded-xl"
            onClick={onSkip}
            disabled={isUploading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content scrollabile */}
        <div className="p-4 sm:p-5 overflow-y-auto flex-1 space-y-3.5 text-xs sm:text-sm overscroll-contain">
          <div className="p-3.5 rounded-2xl bg-amber-50/70 border border-amber-200/80 text-amber-950 space-y-1">
            <p className="font-extrabold text-xs uppercase tracking-wider text-amber-800">
              Desideri scattare o allegare foto?
            </p>
            <p className="text-xs leading-relaxed opacity-90">
              Puoi documentare subito lo stato dell'impianto al tuo arrivo. Le foto verranno inserite nel report complessivo.
            </p>
          </div>

          {/* Griglia foto selezionate */}
          {fotoFiles.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {fotoFiles.map((file, index) => (
                <div key={index} className="relative group rounded-xl border border-slate-200 bg-white overflow-hidden aspect-square">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Foto disservizio ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    className="absolute top-1 right-1 z-10 rounded-full bg-rose-600 text-white p-1 hover:bg-rose-700 shadow-md"
                    onClick={() => handleRemovePhoto(index)}
                    disabled={isUploading}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Selettori foto: Fotocamera e Galleria */}
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-4 text-center space-y-3">
            <div className="flex items-center justify-center gap-3 text-slate-400">
              <Camera className="w-6 h-6 text-slate-400" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">oppure</span>
              <ImageIcon className="w-6 h-6 text-slate-400" />
            </div>

            {/* Input Fotocamera */}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              id="foto-disservizio-camera"
              className="hidden"
              onChange={(e) => {
                handleAddPhotos(Array.from(e.target.files || []));
                e.target.value = '';
              }}
              disabled={isUploading || compressing || fotoFiles.length >= 10}
            />

            {/* Input Galleria */}
            <input
              type="file"
              multiple
              accept="image/*"
              id="foto-disservizio-gallery"
              className="hidden"
              onChange={(e) => {
                handleAddPhotos(Array.from(e.target.files || []));
                e.target.value = '';
              }}
              disabled={isUploading || compressing || fotoFiles.length >= 10}
            />

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
              <label
                htmlFor="foto-disservizio-camera"
                className={`btn-outline inline-flex cursor-pointer text-xs font-bold gap-1.5 bg-white justify-center py-2.5 px-3.5 shadow-2xs ${
                  fotoFiles.length >= 10 || isUploading || compressing ? 'opacity-50 pointer-events-none' : ''
                }`}
              >
                <Camera className="w-4 h-4 text-amber-600" />
                <span>Scatta con fotocamera</span>
              </label>

              <label
                htmlFor="foto-disservizio-gallery"
                className={`btn-outline inline-flex cursor-pointer text-xs font-bold gap-1.5 bg-white justify-center py-2.5 px-3.5 shadow-2xs ${
                  fotoFiles.length >= 10 || isUploading || compressing ? 'opacity-50 pointer-events-none' : ''
                }`}
              >
                <ImageIcon className="w-4 h-4 text-sky-600" />
                <span>Scegli da galleria</span>
              </label>
            </div>

            <p className="text-[11px] text-slate-400">
              {compressing
                ? 'Ottimizzazione immagini in corso…'
                : fotoFiles.length >= 10
                ? 'Limite massimo di 10 foto raggiunto'
                : fotoFiles.length > 0
                ? `${fotoFiles.length}/10 foto allegate · Puoi aggiungerne altre`
                : 'Scatta subito una foto o sceglila dalla galleria del telefono'}
            </p>
          </div>
        </div>

        {/* Buttons fissi */}
        <div className="p-3.5 sm:p-4 bg-slate-50 border-t border-slate-200/90 shrink-0 z-10">
          {fotoFiles.length > 0 ? (
            <button
              type="button"
              className="btn-primary w-full justify-center text-xs sm:text-sm font-extrabold gap-1.5 py-3.5 shadow-md bg-emerald-600 hover:bg-emerald-700 border-emerald-600 min-h-[46px]"
              onClick={handleSend}
              disabled={isUploading || compressing}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Invio foto in corso…</span>
                </>
              ) : (
                <>
                  <span>Invia Foto e Continua</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              className="btn-secondary w-full justify-center text-xs sm:text-sm font-bold gap-1.5 py-3.5 min-h-[46px]"
              onClick={onSkip}
              disabled={isUploading}
            >
              <CameraOff className="w-4 h-4 text-slate-500" />
              <span>Salta senza foto</span>
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
